import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Users, Award, Globe, BookOpen, CheckCircle } from 'lucide-react';
import Marquee from 'react-fast-marquee';

export const HomePage = () => {
  const services = [
    {
      icon: <GraduationCap size={32} />,
      title: 'College Admissions',
      description: 'Expert guidance for public and private college applications across Canada'
    },
    {
      icon: <Globe size={32} />,
      title: 'International Students',
      description: 'Comprehensive support for international students navigating Canadian education'
    },
    {
      icon: <Users size={32} />,
      title: 'Domestic Students',
      description: 'Personalized education pathways for Canadian residents'
    },
    {
      icon: <BookOpen size={32} />,
      title: 'Career Guidance',
      description: 'Strategic course and career planning aligned with your goals'
    }
  ];

  const stats = [
    { number: '1000+', label: 'Success Students' },
    { number: '120+', label: 'Partner Colleges' },
    { number: '98%', label: 'Success Rate' },
    { number: '10+', label: 'Years Experience' }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      country: 'India',
      text: 'ELARA Education helped me navigate the entire process smoothly. I got into my dream college in Toronto!',
      program: 'Business Management'
    },
    {
      name: 'James Chen',
      country: 'China',
      text: 'The consultants were professional and transparent. They guided me every step of the way.',
      program: 'Computer Science'
    },
    {
      name: 'Maria Garcia',
      country: 'Mexico',
      text: 'Best decision I made was choosing ELARA. They made studying in Canada a reality for me.',
      program: 'Healthcare'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center bg-primary text-white overflow-hidden"
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574267033345-66245e88568c?auto=format&fit=crop&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-6" data-testid="hero-subtitle">
                Premium Education Consulting
              </p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="hero-title">
                Your Trusted Education Partner in Canada
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-300 mb-12 max-w-2xl" data-testid="hero-description">
                Navigate your academic journey with confidence. ELARA Education provides expert guidance for college admissions, 
                career pathways, and educational success across Canada.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-consultation"
                  className="px-8 py-4 bg-secondary text-primary font-semibold tracking-wide hover:bg-secondary-light transition-all duration-300 inline-flex items-center justify-center gap-2 group"
                >
                  Book Consultation
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="px-8 py-4 border border-white text-white hover:bg-white hover:text-primary transition-all duration-300 inline-flex items-center justify-center"
                >
                  Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`stat-item-${index}`}
              >
                <h3 className="text-4xl md:text-5xl font-bold text-secondary mb-2">{stat.number}</h3>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">What We Offer</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="services-title">
              Comprehensive Education Services
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From application to admission, we guide you through every step of your educational journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden bg-white p-8 border border-slate-100 hover:border-secondary/50 transition-all duration-500"
                data-testid={`service-card-${index}`}
              >
                <div className="text-secondary mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="view-all-services-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold hover:bg-primary-light transition-all duration-300 group"
            >
              View All Services
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Why ELARA</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="why-choose-title">
                Your Success is Our Mission
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                ELARA Education is a trusted education consulting company in Canada, dedicated to guiding students 
                through the right education pathway according to Canadian education policies and regulations.
              </p>
              <ul className="space-y-4">
                {[
                  'Registered Education Consulting Company',
                  'Partnerships with 120+ colleges across Canada',
                  'Transparent and ethical consulting practices',
                  'Personalized guidance for every student',
                  'Post-admission support and career counseling'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`why-choose-item-${index}`}>
                    <CheckCircle className="text-secondary mt-1 flex-shrink-0" size={20} />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?auto=format&fit=crop&q=80"
                alt="ELARA Education Team"
                className="w-full h-[500px] object-cover"
                data-testid="why-choose-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Success Stories</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="testimonials-title">
              What Our Students Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm p-8 border border-white/20"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="mb-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-secondary text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-slate-200 leading-relaxed mb-6">"{testimonial.text}"</p>
                </div>
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-sm text-slate-300">{testimonial.country} • {testimonial.program}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Colleges Marquee */}
      <section className="py-12 bg-background-alt" data-testid="trusted-colleges-section">
        <div className="mb-8 text-center">
          <p className="text-secondary text-sm tracking-widest uppercase font-semibold">Trusted By</p>
          <h3 className="text-2xl font-semibold mt-2">Partner Colleges Across Canada</h3>
        </div>
        <Marquee gradient={false} speed={40}>
          {[
            'University of Toronto',
            'McGill University',
            'UBC',
            'York University',
            'Humber College',
            'Seneca College',
            'George Brown College',
            'Centennial College'
          ].map((college, index) => (
            <div
              key={index}
              className="mx-8 px-6 py-4 bg-white border border-slate-200 font-semibold text-slate-700"
              data-testid={`college-badge-${index}`}
            >
              {college}
            </div>
          ))}
        </Marquee>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="cta-title">
              Ready to Start Your Journey?
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-12">
              Let ELARA Education guide you to the right college and program. Book a free consultation today.
            </p>
            <Link
              to="/contact"
              data-testid="cta-button"
              className="inline-flex items-center gap-2 px-8 py-4 bg-secondary text-primary font-semibold tracking-wide hover:bg-secondary-light transition-all duration-300 group"
            >
              Book Free Consultation
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};